/**
 * 使用方法:
 *
 * import { createXfyunSpeechRecognizer } from '@/utils'
 *
 * const recognizer = createXfyunSpeechRecognizer({
 *   appId: '你的AppID',
 *   getAuthUrl: async () => {
 *     // 推荐由后端返回讯飞 WebSocket 鉴权地址
 *     return '你的鉴权URL'
 *   }
 * })
 *
 * const removeResultListener = recognizer.addListener('result', ({ text, isFinal }) => {
 *   console.log('识别结果:', text, isFinal)
 * })
 *
 * recognizer.addListener('error', ({ error }) => {
 *   console.error('识别失败:', error)
 * })
 *
 * await recognizer.startListening()
 * recognizer.stopListening()
 * removeResultListener()
 * recognizer.destroy()
 *
 * 事件:
 * start / open / volume / result / partialResult / finalResult / stop / close / error / data / message
 */
const DEFAULT_OPTIONS = {
  protocol: 'wss',
  host: 'iat.xf-yun.com',
  path: '/v1',
  domain: 'slm',
  language: 'zh_cn',
  accent: 'mandarin',
  sampleRate: 16000,
  frameBytes: 1280,
  sendInterval: 40,
  eos: 6000,
  ltc: 2,
  dwa: 'wpgs',
  processorSize: 4096,
  vinfo: 0,
  resId: '',
  dhw: '',
  appId: '7caadf11',
  apiKey: 'eb77c140e6b6351f10c7c4f22ffad318',
  apiSecret: 'ZTFkZTE4OWJmY2RhNmMyZDBkNDBmNTNm'
}

function noop() {}

function isFunction(value) {
  return typeof value === 'function'
}

function isBrowser() {
  return typeof window !== 'undefined'
}

function getAudioContextConstructor() {
  if (!isBrowser()) {
    return null
  }
  return window.AudioContext || window.webkitAudioContext || null
}

function getMediaDevices() {
  if (typeof navigator === 'undefined') {
    return null
  }
  if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
    return navigator.mediaDevices
  }
  return null
}

function getLegacyUserMedia() {
  if (typeof navigator === 'undefined') {
    return null
  }
  return navigator.getUserMedia ||
    navigator.webkitGetUserMedia ||
    navigator.mozGetUserMedia ||
    navigator.msGetUserMedia ||
    null
}

function getMicrophoneStream() {
  const mediaDevices = getMediaDevices()
  if (mediaDevices) {
    return mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        sampleRate: DEFAULT_OPTIONS.sampleRate,
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false
      }
    })
  }

  const legacyUserMedia = getLegacyUserMedia()
  if (!legacyUserMedia) {
    return Promise.reject(new Error('当前浏览器不支持麦克风采集'))
  }

  return new Promise((resolve, reject) => {
    legacyUserMedia.call(
      navigator,
      {
        audio: {
          channelCount: 1,
          sampleRate: DEFAULT_OPTIONS.sampleRate
        }
      },
      resolve,
      reject
    )
  })
}

function concatUint8Arrays(left, right) {
  if (!left || !left.length) {
    return right || new Uint8Array(0)
  }
  if (!right || !right.length) {
    return left
  }
  const result = new Uint8Array(left.length + right.length)
  result.set(left, 0)
  result.set(right, left.length)
  return result
}

function int16ArrayToUint8Array(int16Array) {
  const result = new Uint8Array(int16Array.length * 2)
  for (let index = 0; index < int16Array.length; index++) {
    const value = int16Array[index]
    result[index * 2] = value & 0xff
    result[index * 2 + 1] = (value >> 8) & 0xff
  }
  return result
}

function downsampleToInt16(float32Array, inputSampleRate, outputSampleRate) {
  if (!float32Array || !float32Array.length) {
    return new Int16Array(0)
  }

  const sampleRate = inputSampleRate || outputSampleRate
  if (outputSampleRate > sampleRate) {
    throw new Error('输出采样率不能高于输入采样率')
  }

  const ratio = sampleRate / outputSampleRate
  const outputLength = Math.round(float32Array.length / ratio)
  const result = new Int16Array(outputLength)
  let offsetResult = 0
  let offsetBuffer = 0

  while (offsetResult < outputLength) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio)
    let accumulated = 0
    let count = 0
    for (let index = offsetBuffer; index < nextOffsetBuffer && index < float32Array.length; index++) {
      accumulated += float32Array[index]
      count++
    }
    let sample = count ? accumulated / count : 0
    sample = Math.max(-1, Math.min(1, sample))
    result[offsetResult] = sample < 0 ? sample * 0x8000 : sample * 0x7fff
    offsetResult++
    offsetBuffer = nextOffsetBuffer
  }

  return result
}

function calcVolume(float32Array) {
  if (!float32Array || !float32Array.length) {
    return 0
  }

  let sum = 0
  for (let index = 0; index < float32Array.length; index++) {
    const sample = float32Array[index]
    sum += sample * sample
  }
  const rms = Math.sqrt(sum / float32Array.length)
  return Math.min(100, Math.round(rms * 180))
}

function uint8ArrayToBase64(bytes) {
  if (!bytes || !bytes.length) {
    return ''
  }
  let binary = ''
  const chunkSize = 0x8000
  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize)
    binary += String.fromCharCode.apply(null, chunk)
  }
  return window.btoa(binary)
}

function base64ToUtf8(base64Value) {
  if (!base64Value) {
    return ''
  }
  const binary = window.atob(base64Value)
  let percentEncoded = ''
  for (let index = 0; index < binary.length; index++) {
    const code = binary.charCodeAt(index).toString(16).toUpperCase()
    percentEncoded += '%' + ('00' + code).slice(-2)
  }
  return decodeURIComponent(percentEncoded)
}

function stringToUtf8Bytes(content) {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(content)
  }
  const encoded = unescape(encodeURIComponent(content))
  const result = new Uint8Array(encoded.length)
  for (let index = 0; index < encoded.length; index++) {
    result[index] = encoded.charCodeAt(index)
  }
  return result
}

function arrayBufferToBase64(buffer) {
  return uint8ArrayToBase64(new Uint8Array(buffer))
}

function padRfc1123Date(date) {
  return date.toUTCString()
}

function parseResultText(resultObject) {
  if (!resultObject || !Array.isArray(resultObject.ws)) {
    return ''
  }
  return resultObject.ws.map(item => {
    if (!item || !Array.isArray(item.cw)) {
      return ''
    }
    return item.cw.map(wordItem => wordItem && wordItem.w ? wordItem.w : '').join('')
  }).join('')
}

function createEventPayload(payload, extra) {
  return Object.assign({}, payload || {}, extra || {})
}

async function hmacSha256Base64(message, secret) {
  if (!isBrowser() || !window.crypto || !window.crypto.subtle) {
    throw new Error('当前环境不支持前端签名，请改用后端下发鉴权地址')
  }

  const key = await window.crypto.subtle.importKey(
    'raw',
    stringToUtf8Bytes(secret),
    {
      name: 'HMAC',
      hash: {
        name: 'SHA-256'
      }
    },
    false,
    ['sign']
  )

  const signature = await window.crypto.subtle.sign('HMAC', key, stringToUtf8Bytes(message))
  return arrayBufferToBase64(signature)
}

export async function buildXfyunIatAuthUrl(options = {}) {
  const protocol = options.protocol || DEFAULT_OPTIONS.protocol
  const host = options.host || DEFAULT_OPTIONS.host
  const path = options.path || DEFAULT_OPTIONS.path
  const apiKey = options.apiKey
  const apiSecret = options.apiSecret

  if (!apiKey || !apiSecret) {
    throw new Error('缺少讯飞语音识别 APIKey 或 APISecret')
  }

  const date = padRfc1123Date(new Date())
  const requestLine = 'GET ' + path + ' HTTP/1.1'
  const signatureOrigin = 'host: ' + host + '\n' + 'date: ' + date + '\n' + requestLine
  const signature = await hmacSha256Base64(signatureOrigin, apiSecret)
  const authorizationOrigin = 'api_key="' + apiKey + '", algorithm="hmac-sha256", headers="host date request-line", signature="' + signature + '"'
  const authorization = window.btoa(authorizationOrigin)

  return protocol + '://' + host + path +
    '?authorization=' + encodeURIComponent(authorization) +
    '&date=' + encodeURIComponent(date) +
    '&host=' + encodeURIComponent(host)
}

class XfyunSpeechRecognizer {
  constructor(options = {}) {
    this.options = Object.assign({}, DEFAULT_OPTIONS, options)
    this.listeners = {}
    this.resetRuntimeState()
  }

  resetRuntimeState() {
    this.socket = null
    this.audioContext = null
    this.mediaStream = null
    this.mediaSourceNode = null
    this.processorNode = null
    this.silentGainNode = null
    this.frameTimer = null
    this.pendingAudioBytes = new Uint8Array(0)
    this.audioFrames = []
    this.segmentStore = {}
    this.resultText = ''
    this.sequence = 1
    this.hasSentFirstFrame = false
    this.hasSentFinalFrame = false
    this.stopRequested = false
    this.isStarting = false
    this.isListening = false
    this.isDestroyed = false
  }

  static isSupported() {
    return Boolean(
      isBrowser() &&
      typeof window.WebSocket !== 'undefined' &&
      getAudioContextConstructor() &&
      (getMediaDevices() || getLegacyUserMedia())
    )
  }

  updateOptions(nextOptions = {}) {
    this.options = Object.assign({}, this.options, nextOptions)
    return this
  }

  addListener(eventName, handler) {
    if (!eventName || !isFunction(handler)) {
      return noop
    }
    if (!this.listeners[eventName]) {
      this.listeners[eventName] = new Set()
    }
    this.listeners[eventName].add(handler)
    return () => this.removeListener(eventName, handler)
  }

  on(eventName, handler) {
    return this.addListener(eventName, handler)
  }

  removeListener(eventName, handler) {
    const eventListeners = this.listeners[eventName]
    if (!eventListeners) {
      return this
    }
    if (!handler) {
      eventListeners.clear()
      return this
    }
    eventListeners.delete(handler)
    return this
  }

  off(eventName, handler) {
    return this.removeListener(eventName, handler)
  }

  removeAllListeners(eventName) {
    if (eventName) {
      this.removeListener(eventName)
      return this
    }
    Object.keys(this.listeners).forEach(key => {
      this.listeners[key].clear()
    })
    return this
  }

  emit(eventName, payload) {
    const eventListeners = this.listeners[eventName]
    if (!eventListeners || !eventListeners.size) {
      return
    }
    eventListeners.forEach(listener => {
      try {
        listener(payload)
      } catch (error) {
        console.error('[xfyunSpeechRecognizer] listener error:', error)
      }
    })
  }

  async startListening() {
    if (this.isDestroyed) {
      throw new Error('语音识别实例已销毁，不能再次启动')
    }
    if (!XfyunSpeechRecognizer.isSupported()) {
      throw new Error('当前浏览器不支持讯飞语音识别所需能力')
    }
    if (!this.options.appId) {
      throw new Error('缺少讯飞语音识别 AppID')
    }
    if (this.isStarting || this.isListening) {
      return this
    }

    this.resetSessionState()
    this.isStarting = true

    try {
      const authUrl = await this.resolveAuthUrl()
      const stream = await getMicrophoneStream()
      await this.setupAudioRecorder(stream)
      await this.openSocket(authUrl)
      this.isStarting = false
      this.isListening = true
      this.emit('start', createEventPayload({
        authUrl: authUrl
      }))
      return this
    } catch (error) {
      this.isStarting = false
      this.handleError(error)
      this.releaseAudioResources()
      this.releaseSocketResources()
      throw error
    }
  }

  stopListening() {
    if (this.isDestroyed) {
      return
    }
    this.stopRequested = true
    this.releaseAudioResources()
    if (!this.socket || this.socket.readyState !== window.WebSocket.OPEN) {
      this.finishSession({
        type: 'stop'
      })
    }
  }

  destroy() {
    if (this.isDestroyed) {
      return
    }
    this.isDestroyed = true
    this.stopRequested = true
    this.releaseAudioResources()
    this.forceCloseSocket()
    this.clearFrameTimer()
    this.removeAllListeners()
  }

  async resolveAuthUrl() {
    if (this.options.authUrl) {
      return this.options.authUrl
    }
    if (isFunction(this.options.getAuthUrl)) {
      const authUrl = await this.options.getAuthUrl(this.options)
      if (!authUrl) {
        throw new Error('getAuthUrl 未返回有效的鉴权地址')
      }
      return authUrl
    }
    return buildXfyunIatAuthUrl(this.options)
  }

  resetSessionState() {
    this.clearFrameTimer()
    this.pendingAudioBytes = new Uint8Array(0)
    this.audioFrames = []
    this.segmentStore = {}
    this.resultText = ''
    this.sequence = 1
    this.hasSentFirstFrame = false
    this.hasSentFinalFrame = false
    this.stopRequested = false
  }

  async setupAudioRecorder(stream) {
    const AudioContextConstructor = getAudioContextConstructor()
    if (!AudioContextConstructor) {
      throw new Error('当前浏览器不支持 AudioContext')
    }

    this.mediaStream = stream
    this.audioContext = new AudioContextConstructor()
    if (this.audioContext.state === 'suspended' && this.audioContext.resume) {
      await this.audioContext.resume()
    }

    this.mediaSourceNode = this.audioContext.createMediaStreamSource(stream)
    this.processorNode = this.audioContext.createScriptProcessor(this.options.processorSize, 1, 1)
    this.silentGainNode = this.audioContext.createGain()
    this.silentGainNode.gain.value = 0

    this.processorNode.onaudioprocess = event => {
      if (this.stopRequested) {
        return
      }

      const audioData = event.inputBuffer.getChannelData(0)
      const volume = calcVolume(audioData)
      this.emit('volume', {
        volume: volume
      })

      const pcm16Data = downsampleToInt16(audioData, this.audioContext.sampleRate, this.options.sampleRate)
      if (!pcm16Data.length) {
        return
      }
      const pcmBytes = int16ArrayToUint8Array(pcm16Data)
      this.enqueueAudioBytes(pcmBytes)
    }

    this.mediaSourceNode.connect(this.processorNode)
    this.processorNode.connect(this.silentGainNode)
    this.silentGainNode.connect(this.audioContext.destination)
  }

  enqueueAudioBytes(pcmBytes) {
    this.pendingAudioBytes = concatUint8Arrays(this.pendingAudioBytes, pcmBytes)
    while (this.pendingAudioBytes.length >= this.options.frameBytes) {
      this.audioFrames.push(this.pendingAudioBytes.slice(0, this.options.frameBytes))
      this.pendingAudioBytes = this.pendingAudioBytes.slice(this.options.frameBytes)
    }
  }

  openSocket(authUrl) {
    return new Promise((resolve, reject) => {
      const socket = new window.WebSocket(authUrl)
      let opened = false

      socket.onopen = () => {
        opened = true
        this.socket = socket
        this.startFramePump()
        this.emit('open', {
          url: authUrl
        })
        resolve()
      }

      socket.onerror = event => {
        const error = new Error('讯飞语音识别连接失败')
        if (!opened) {
          reject(error)
          return
        }
        this.handleError(error, event)
      }

      socket.onmessage = event => {
        if (!opened) {
          return
        }
        this.handleSocketMessage(event)
      }

      socket.onclose = event => {
        if (!opened) {
          reject(new Error('讯飞语音识别连接已关闭'))
          return
        }
        this.handleSocketClose(event)
      }
    })
  }

  startFramePump() {
    this.clearFrameTimer()
    this.frameTimer = setInterval(() => {
      this.flushAudioFrame()
    }, this.options.sendInterval)
  }

  clearFrameTimer() {
    if (this.frameTimer) {
      clearInterval(this.frameTimer)
      this.frameTimer = null
    }
  }

  flushAudioFrame() {
    if (!this.socket || this.socket.readyState !== window.WebSocket.OPEN || this.hasSentFinalFrame) {
      return
    }

    if (this.audioFrames.length) {
      const frame = this.audioFrames.shift()
      const status = this.hasSentFirstFrame ? 1 : 0
      this.sendAudioFrame(frame, status)
      this.hasSentFirstFrame = true
      return
    }

    if (this.stopRequested) {
      if (this.pendingAudioBytes.length) {
        const frame = this.pendingAudioBytes
        this.pendingAudioBytes = new Uint8Array(0)
        const status = this.hasSentFirstFrame ? 1 : 0
        this.sendAudioFrame(frame, status)
        this.hasSentFirstFrame = true
        return
      }

      if (!this.hasSentFirstFrame) {
        this.sendAudioFrame(new Uint8Array(0), 0)
        this.hasSentFirstFrame = true
        return
      }

      this.sendAudioFrame(new Uint8Array(0), 2)
      this.hasSentFinalFrame = true
      this.clearFrameTimer()
    }
  }

  sendAudioFrame(frame, status) {
    if (!this.socket || this.socket.readyState !== window.WebSocket.OPEN) {
      return
    }

    const header = {
      app_id: this.options.appId,
      status: status
    }
    if (this.options.resId) {
      header.res_id = this.options.resId
    }

    const payload = {
      audio: {
        encoding: 'raw',
        sample_rate: this.options.sampleRate,
        channels: 1,
        bit_depth: 16,
        seq: this.sequence++,
        status: status,
        audio: uint8ArrayToBase64(frame)
      }
    }

    const requestData = {
      header: header,
      payload: payload
    }

    if (status === 0) {
      requestData.parameter = {
        iat: {
          domain: this.options.domain,
          language: this.options.language,
          accent: this.options.accent,
          eos: this.options.eos,
          ltc: this.options.ltc,
          vinfo: this.options.vinfo,
          dwa: this.options.dwa,
          result: {
            encoding: 'utf8',
            compress: 'raw',
            format: 'json'
          }
        }
      }
      if (this.options.dhw) {
        requestData.parameter.iat.dhw = this.options.dhw
      }
    }

    this.socket.send(JSON.stringify(requestData))
    this.emit('data', {
      direction: 'send',
      status: status,
      seq: payload.audio.seq
    })
  }

  handleSocketMessage(event) {
    try {
      const response = JSON.parse(event.data)
      const header = response.header || {}

      if (header.code !== 0) {
        throw new Error(header.message || '讯飞语音识别返回异常')
      }

      const payload = response.payload || {}
      const resultPayload = payload.result
      if (!resultPayload || !resultPayload.text) {
        this.emit('message', response)
        return
      }

      const decodedText = base64ToUtf8(resultPayload.text)
      const resultObject = JSON.parse(decodedText)
      const mergedText = this.mergeRecognitionResult(resultObject)
      const eventPayload = {
        text: mergedText,
        chunkText: parseResultText(resultObject),
        isFinal: Boolean(resultObject.ls || resultPayload.status === 2 || header.status === 2),
        sid: header.sid,
        raw: response,
        result: resultObject
      }

      this.resultText = mergedText
      this.emit('result', eventPayload)

      if (eventPayload.isFinal) {
        this.emit('finalResult', eventPayload)
      } else {
        this.emit('partialResult', eventPayload)
      }
    } catch (error) {
      this.handleError(error, event)
    }
  }

  mergeRecognitionResult(resultObject) {
    const text = parseResultText(resultObject)
    const segmentSn = Number(resultObject.sn)

    if (isNaN(segmentSn)) {
      return this.resultText + text
    }

    if (resultObject.pgs === 'rpl' && Array.isArray(resultObject.rg) && resultObject.rg.length === 2) {
      const start = Number(resultObject.rg[0])
      const end = Number(resultObject.rg[1])
      Object.keys(this.segmentStore).forEach(key => {
        const numericKey = Number(key)
        if (numericKey >= start && numericKey <= end) {
          delete this.segmentStore[key]
        }
      })
    }

    this.segmentStore[segmentSn] = text
    return Object.keys(this.segmentStore)
      .sort((left, right) => Number(left) - Number(right))
      .map(key => this.segmentStore[key])
      .join('')
  }

  handleSocketClose(event) {
    const shouldEmitStop = this.isListening || this.isStarting
    this.releaseSocketResources()
    this.releaseAudioResources()
    if (shouldEmitStop) {
      this.emit('stop', {
        reason: this.stopRequested ? 'normal' : 'socket-close',
        event: event,
        text: this.resultText
      })
    }
    this.emit('close', {
      event: event
    })
  }

  releaseSocketResources() {
    this.clearFrameTimer()
    if (this.socket) {
      this.socket.onopen = null
      this.socket.onmessage = null
      this.socket.onerror = null
      this.socket.onclose = null
      this.socket = null
    }
    this.isStarting = false
    this.isListening = false
  }

  releaseAudioResources() {
    if (this.processorNode) {
      this.processorNode.onaudioprocess = null
      this.processorNode.disconnect()
      this.processorNode = null
    }

    if (this.mediaSourceNode) {
      this.mediaSourceNode.disconnect()
      this.mediaSourceNode = null
    }

    if (this.silentGainNode) {
      this.silentGainNode.disconnect()
      this.silentGainNode = null
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => {
        track.stop()
      })
      this.mediaStream = null
    }

    if (this.audioContext) {
      const currentAudioContext = this.audioContext
      this.audioContext = null
      if (currentAudioContext.close) {
        const closeResult = currentAudioContext.close()
        if (closeResult && isFunction(closeResult.catch)) {
          closeResult.catch(noop)
        }
      }
    }
  }

  forceCloseSocket() {
    if (!this.socket) {
      return
    }
    try {
      if (this.socket.readyState === window.WebSocket.OPEN || this.socket.readyState === window.WebSocket.CONNECTING) {
        this.socket.close()
      }
    } catch (error) {
      console.error('[xfyunSpeechRecognizer] close socket error:', error)
    } finally {
      this.releaseSocketResources()
    }
  }

  finishSession(extraPayload) {
    this.releaseSocketResources()
    this.releaseAudioResources()
    this.emit('stop', createEventPayload({
      reason: 'manual-stop',
      text: this.resultText
    }, extraPayload))
  }

  handleError(error, rawEvent) {
    this.emit('error', {
      error: error,
      event: rawEvent || null
    })
  }
}

export function createXfyunSpeechRecognizer(options) {
  return new XfyunSpeechRecognizer(options)
}

export default XfyunSpeechRecognizer



// WEBPACK FOOTER //
// ./src/utils/xfyunSpeechRecognizer.js